import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, FileText, Send, Copy, Check, Archive, Trash2, RefreshCw, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { absoluteMediaUrl } from "@/components/ImageField";

const STATUS_LABELS = {
  "nuova": "Nuova",
  "stima inviata automaticamente": "Inviata automaticamente",
  "da verificare": "Da verificare",
  "modificata": "Modificata",
  "confermata": "Confermata",
  "preventivo definitivo inviato": "Preventivo definitivo inviato",
  "accettata": "Accettata",
  "rifiutata": "Rifiutata",
  "archiviata": "Archiviata",
};
const STATUS_COLORS = {
  "nuova": "text-gold border-gold/50",
  "stima inviata automaticamente": "text-blue-700 border-blue-700/40",
  "da verificare": "text-amber-700 border-amber-700/40",
  "modificata": "text-taupe border-hair",
  "confermata": "text-emerald-700 border-emerald-700/40",
  "preventivo definitivo inviato": "text-emerald-700 border-emerald-700/40",
  "accettata": "text-emerald-700 border-emerald-700/40",
  "rifiutata": "text-red-700 border-red-700/40",
  "archiviata": "text-taupe border-hair",
};

const eur = (n) => (n === null || n === undefined || n === "") ? "—" :
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n));
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

function Row({ label, value, testId }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-hair/60 last:border-0" data-testid={testId}>
      <span className="text-[10px] tracking-[0.14em] uppercase text-taupe shrink-0">{label}</span>
      <span className="text-sm text-ink text-right break-words">{value}</span>
    </div>
  );
}

function MoneyField({ label, field, form, setForm, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">{label}</Label>
      <Input type="number" step="0.01" value={form.pricing[field] ?? ""}
        onChange={(e) => setForm({ ...form, pricing: { ...form.pricing, [field]: e.target.value === "" ? null : Number(e.target.value) } })}
        className="rounded-none mt-1" data-testid={`${testId}-input`} />
    </div>
  );
}

export function EstimatesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("tutti");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ to: "", subject: "", body: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/estimates");
      setItems(data);
      if (selected) {
        const cur = data.find((x) => x.id === selected.id);
        if (cur) { setSelected(cur); setForm(toForm(cur)); }
      }
    } catch { toast.error("Errore nel caricamento delle stime"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toForm = (e) => ({
    status: e.status,
    summary_text: e.summary_text || "",
    notes_internal: e.notes_internal || "",
    notes_client: e.notes_client || "",
    pricing: { ...(e.pricing || {}) },
  });

  const open = (e) => { setSelected(e); setForm(toForm(e)); };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/estimates/${selected.id}`, {
        status: form.status, summary_text: form.summary_text,
        notes_internal: form.notes_internal, notes_client: form.notes_client, pricing: form.pricing,
      });
      toast.success("Stima aggiornata");
      setSelected(data); setForm(toForm(data)); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const act = async (action, okMsg) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/estimates/${selected.id}/${action}`);
      toast.success(okMsg);
      setSelected(data); setForm(toForm(data)); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };

  const saveNotes = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/estimates/${selected.id}`, {
        notes_internal: form.notes_internal, notes_client: form.notes_client,
      });
      toast.success("Note salvate");
      setSelected(data); setForm(toForm(data)); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore salvataggio note"); }
    finally { setBusy(false); }
  };

  const genPdf = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/estimates/${selected.id}/pdf`);
      toast.success("PDF generato");
      setSelected({ ...selected, pdf_url: data.pdf_url }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore generazione PDF"); }
    finally { setBusy(false); }
  };

  const previewPdf = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/estimates/${selected.id}/pdf`);
      setSelected({ ...selected, pdf_url: data.pdf_url }); load();
      window.open(absoluteMediaUrl(data.pdf_url), "_blank", "noopener");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore anteprima PDF"); }
    finally { setBusy(false); }
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/estimates/${selected.id}/duplicate`);
      toast.success("Stima duplicata");
      setSelected(null); load();
    } catch { toast.error("Errore duplicazione"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare definitivamente la stima ${selected.number}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/estimates/${selected.id}`);
      toast.success("Stima eliminata");
      setSelected(null); load();
    } catch { toast.error("Errore eliminazione"); }
    finally { setBusy(false); }
  };

  const openSend = () => {
    setSendForm({
      to: selected.client?.email || "",
      subject: `Stima ${selected.number} — Giovanna Matino Interior`,
      body: `Gentile ${selected.client?.name || ""},\n\nin allegato trovi la stima preliminare ${selected.number}, elaborata sulla base delle informazioni che ci hai fornito.\n\nRestiamo a disposizione per qualsiasi chiarimento.`,
    });
    setSendOpen(true);
  };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/estimates/${selected.id}/send`, sendForm);
      if (data.success) { toast.success("Email inviata"); setSendOpen(false); load(); }
      else toast.error("Invio email non riuscito (controlla la configurazione email)");
    } catch (err) { toast.error(err.response?.data?.detail || "Errore invio"); }
    finally { setBusy(false); }
  };

  const filtered = filter === "tutti" ? items : items.filter((x) => x.status === filter);
  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";
  const btnInk = `${btn} bg-ink text-bone hover:bg-gold`;
  const btnGhost = `${btn} border border-ink hover:bg-ink hover:text-bone`;

  if (selected && form) {
    const p = selected.project || {};
    const pr = form.pricing;
    const c = selected.client || {};
    return (
      <div className="space-y-6" data-testid="estimate-detail">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <button onClick={() => setSelected(null)} className={btnGhost} data-testid="estimate-back-btn">
            <ArrowLeft size={14} /> Tutte le stime
          </button>
          <span className={`text-[10px] tracking-[0.14em] uppercase border px-3 py-1.5 ${STATUS_COLORS[selected.status] || "text-taupe border-hair"}`} data-testid="estimate-status-badge">
            {STATUS_LABELS[selected.status] || selected.status}
          </span>
        </div>

        <div className="bg-white border border-hair p-5 md:p-6">
          <div className="flex justify-between items-start flex-wrap gap-2">
            <div>
              <div className="eyebrow">{selected.number}</div>
              <p className="text-sm text-taupe mt-1">{fmtDate(selected.created_at)} · {selected.origin || "Configuratore pubblico"}</p>
            </div>
            {selected.priority && <span className="text-[10px] tracking-[0.14em] uppercase bg-gold/15 text-gold px-3 py-1.5" data-testid="estimate-priority">{selected.priority}</span>}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-hair p-5 md:p-6">
            <div className="eyebrow mb-3">Cliente</div>
            <Row label="Nome" value={`${c.name || ""} ${c.surname || ""}`.trim()} testId="estimate-client-name" />
            <Row label="Email" value={c.email} testId="estimate-client-email" />
            <Row label="Telefono" value={c.phone} testId="estimate-client-phone" />
            <Row label="Azienda" value={c.company} />
            <Row label="Contatto pref." value={c.contact_preference} />
            <Row label="Consenso privacy" value={selected.consent?.privacy ? `Sì (${selected.consent?.privacy_version || "v1"} · ${fmtDate(selected.consent?.privacy_accepted_at)})` : "Non registrato"} testId="estimate-consent-privacy" />
            <Row label="Consenso marketing" value={selected.consent?.newsletter ? "Sì" : "No"} testId="estimate-consent-marketing" />
          </div>
          <div className="bg-white border border-hair p-5 md:p-6">
            <div className="eyebrow mb-3">Progetto</div>
            <Row label="Servizio" value={selected.pricing?.package || p.package_key} testId="estimate-package" />
            <Row label="Immobile" value={p.property_type} />
            <Row label="Superficie" value={p.sqm ? `${p.sqm} m²` : null} testId="estimate-sqm" />
            <Row label="Comune" value={[p.comune, p.cap].filter(Boolean).join(" ")} />
            <Row label="Intervento" value={p.intervention_type} />
            <Row label="Obiettivo" value={p.goal} />
            <Row label="Tempistica" value={p.timeline} />
            <Row label="Urgenza" value={p.urgency ? "Sì" : null} testId="estimate-urgency" />
            <Row label="Trasferta" value={selected.pricing?.travel_label} />
            {p.message && <p className="text-sm text-taupe mt-3 whitespace-pre-wrap">{p.message}</p>}
            {selected.attachment_url && (
              <a href={absoluteMediaUrl(selected.attachment_url)} target="_blank" rel="noreferrer"
                className="inline-block mt-3 text-sm text-gold hover:underline" data-testid="estimate-attachment-link">Apri allegato del cliente</a>
            )}
          </div>
        </div>

        <div className="bg-white border border-hair p-5 md:p-6">
          <div className="eyebrow mb-3">Riepilogo inviato al cliente</div>
          <Textarea rows={5} value={form.summary_text} onChange={(e) => setForm({ ...form, summary_text: e.target.value })}
            className="rounded-none" data-testid="estimate-summary-input" />
          <div className="mt-5 pt-4 border-t border-hair space-y-4">
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Note per il cliente (visibili in anteprima, nel PDF e nell'invio)</Label>
              <Textarea rows={3} value={form.notes_client} onChange={(e) => setForm({ ...form, notes_client: e.target.value })}
                className="rounded-none mt-1" data-testid="estimate-notes-client-input"
                placeholder="Es. orari preferiti per il sopralluogo, dettagli da verificare insieme…" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Note interne (solo dashboard — mai nel PDF, mai al cliente)</Label>
              <Textarea rows={2} value={form.notes_internal} onChange={(e) => setForm({ ...form, notes_internal: e.target.value })}
                className="rounded-none mt-1" data-testid="estimate-notes-internal-input" />
              {selected.notes_internal_meta?.at && (
                <p className="text-[11px] text-taupe mt-1" data-testid="estimate-notes-internal-meta">
                  Ultima modifica: {fmtDate(selected.notes_internal_meta.at)} · {selected.notes_internal_meta.by}
                </p>
              )}
            </div>
            <button onClick={saveNotes} disabled={busy}
              className="min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase border border-ink px-4 py-2 hover:bg-ink hover:text-bone transition-colors disabled:opacity-60"
              data-testid="estimate-save-notes-btn">Salva note</button>
          </div>
        </div>

        <div className="bg-white border border-hair p-5 md:p-6">
          <div className="eyebrow mb-3">Economia {pr.manual && <span className="normal-case text-gold">— stima da elaborare su misura</span>}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MoneyField label="Compenso base" field="base" form={form} setForm={setForm} testId="estimate-price-base" />
            <MoneyField label={`Urgenza (${pr.urgency_percent ?? 0}%)`} field="urgency_amount" form={form} setForm={setForm} testId="estimate-price-urgency" />
            <MoneyField label="Trasferta" field="travel_cost" form={form} setForm={setForm} testId="estimate-price-travel" />
            <MoneyField label={`Contributo (${pr.contribution_percent ?? 0}%)`} field="contribution" form={form} setForm={setForm} testId="estimate-price-contribution" />
            <MoneyField label={`IVA (${pr.vat_rate ?? 0}%)`} field="vat" form={form} setForm={setForm} testId="estimate-price-vat" />
            <MoneyField label="Bollo" field="bollo" form={form} setForm={setForm} testId="estimate-price-bollo" />
            <MoneyField label="Totale" field="total" form={form} setForm={setForm} testId="estimate-price-total" />
            <div data-testid="estimate-status-field">
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Stato</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="estimate-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(STATUS_LABELS).map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-xs text-taupe mt-3">Ogni modifica a importi o riepilogo viene salvata nello storico versioni: il documento precedente non viene sovrascritto.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={save} disabled={busy} className={btnInk} data-testid="estimate-save-btn"><Check size={14} /> Salva modifiche</button>
          <button onClick={previewPdf} disabled={busy} className={btnInk} data-testid="estimate-preview-btn"><Eye size={14} /> Anteprima PDF</button>
          <button onClick={genPdf} disabled={busy} className={btnGhost} data-testid="estimate-pdf-btn"><FileText size={14} /> Rigenera PDF</button>
          {selected.pdf_url && (
            <a href={absoluteMediaUrl(selected.pdf_url)} target="_blank" rel="noreferrer" className={btnGhost} data-testid="estimate-pdf-open">Apri PDF</a>
          )}
          <button onClick={openSend} disabled={busy || !selected.pdf_url} className={btnGhost} data-testid="estimate-send-btn"><Send size={14} /> Invia versione aggiornata</button>
          <button onClick={duplicate} disabled={busy} className={btnGhost} data-testid="estimate-duplicate-btn"><Copy size={14} /> Duplica</button>
          {selected.status !== "confermata" && (
            <button onClick={() => act("confirm", "Stima confermata")} disabled={busy} className={btnGhost} data-testid="estimate-confirm-btn"><Check size={14} /> Conferma</button>
          )}
          {selected.status !== "archiviata" && (
            <button onClick={() => act("archive", "Stima archiviata")} disabled={busy} className={btnGhost} data-testid="estimate-archive-btn"><Archive size={14} /> Archivia</button>
          )}
          <button onClick={remove} disabled={busy} className={`${btn} border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="estimate-delete-btn"><Trash2 size={14} /> Elimina</button>
        </div>

        {(selected.status_history?.length > 0 || selected.versions?.length > 0 || selected.sent_log?.length > 0) && (
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white border border-hair p-5" data-testid="estimate-history">
              <div className="eyebrow mb-3">Storico stati</div>
              {(selected.status_history || []).slice().reverse().map((h, i) => (
                <p key={i} className="text-xs text-taupe py-1 border-b border-hair/60 last:border-0">
                  {fmtDate(h.at)} — {STATUS_LABELS[h.status] || h.status}{h.note ? ` · ${h.note}` : ""}
                </p>
              ))}
            </div>
            <div className="bg-white border border-hair p-5" data-testid="estimate-versions">
              <div className="eyebrow mb-3">Versioni precedenti</div>
              {(selected.versions || []).length === 0 && <p className="text-xs text-taupe">Nessuna modifica registrata.</p>}
              {(selected.versions || []).slice().reverse().map((v, i) => (
                <div key={i} className="text-xs text-taupe py-1 border-b border-hair/60 last:border-0">
                  <p>v{v.n} · {fmtDate(v.at)}</p>
                  {(v.changed || []).map((c2, j) => {
                    const num = (x) => typeof x === "number" && isFinite(x);
                    const fmt = (x) => num(x) ? eur(x) : String(x ?? "—");
                    return (
                      <p key={j} className="pl-2">{c2.field}: {fmt(c2.from)} → {fmt(c2.to)}</p>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className="bg-white border border-hair p-5" data-testid="estimate-sent-log">
              <div className="eyebrow mb-3">Invii email</div>
              {(selected.sent_log || []).length === 0 && <p className="text-xs text-taupe">Nessun invio manuale.</p>}
              {(selected.sent_log || []).slice().reverse().map((s, i) => (
                <p key={i} className="text-xs text-taupe py-1 border-b border-hair/60 last:border-0">
                  {fmtDate(s.at)} — {s.to} · {s.result}
                </p>
              ))}
              <p className="text-xs text-taupe mt-2">Email automatica iniziale: {selected.email_sent ? "inviata" : "non inviata"}</p>
            </div>
          </div>
        )}

        <Dialog open={sendOpen} onOpenChange={setSendOpen}>
          <DialogContent className="rounded-none max-w-lg" data-testid="estimate-send-dialog">
            <DialogHeader><DialogTitle className="font-serif">Invia stima via email</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Destinatario</Label>
                <Input value={sendForm.to} onChange={(e) => setSendForm({ ...sendForm, to: e.target.value })} className="rounded-none mt-1" data-testid="estimate-send-to" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Oggetto</Label>
                <Input value={sendForm.subject} onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })} className="rounded-none mt-1" data-testid="estimate-send-subject" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Messaggio</Label>
                <Textarea rows={6} value={sendForm.body} onChange={(e) => setSendForm({ ...sendForm, body: e.target.value })} className="rounded-none mt-1" data-testid="estimate-send-body" />
              </div>
              <p className="text-xs text-taupe">Il PDF della stima e il link per richiedere la conferma dello Studio vengono allegati automaticamente.</p>
              <button onClick={send} disabled={busy} className={btnInk} data-testid="estimate-send-confirm"><Send size={14} /> Invia</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="estimates-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="rounded-none w-[220px] bg-white" data-testid="estimates-filter">
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti gli stati</SelectItem>
            {Object.keys(STATUS_LABELS).map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={load} className="min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase border border-ink px-4 hover:bg-ink hover:text-bone transition-colors" data-testid="estimates-refresh-btn">
          <RefreshCw size={14} /> Aggiorna
        </button>
      </div>

      {loading && <p className="text-sm text-taupe">Caricamento…</p>}
      {!loading && filtered.length === 0 && (
        <div className="bg-white border border-hair p-8 text-center" data-testid="estimates-empty">
          <p className="text-sm text-taupe">Nessuna stima {filter !== "tutti" ? "con questo stato" : "ricevuta finora"}.</p>
          <p className="text-xs text-taupe mt-2">Le richieste inviate dal configuratore pubblico appariranno qui.</p>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((e) => (
          <button key={e.id} onClick={() => open(e)}
            className="w-full text-left bg-white border border-hair p-4 md:p-5 hover:border-gold transition-colors"
            data-testid={`estimate-row-${e.id}`}>
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-serif text-lg">{e.number}</span>
                  <span className={`text-[10px] tracking-[0.14em] uppercase border px-2 py-0.5 ${STATUS_COLORS[e.status] || "text-taupe border-hair"}`}>
                    {STATUS_LABELS[e.status] || e.status}
                  </span>
                  {e.priority && <span className="text-[10px] tracking-[0.14em] uppercase bg-gold/15 text-gold px-2 py-0.5">{e.priority}</span>}
                </div>
                <p className="text-sm text-taupe mt-1 truncate">
                  {e.client?.name} {e.client?.surname} · {e.pricing?.package || e.project?.package_key}
                  {e.project?.sqm ? ` · ${e.project.sqm} m²` : ""}{e.project?.comune ? ` · ${e.project.comune}` : ""}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-serif text-lg" data-testid={`estimate-total-${e.id}`}>
                  {e.pricing?.manual ? "Su misura" : eur(e.pricing?.total)}
                </p>
                <p className="text-xs text-taupe">{fmtDate(e.created_at)}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
